<template>
  <div
    class="form-group"
    :class="{
      'form-group--has-error': localError,
      'form-group--has-success': !localError && touched,
    }"
  >
    <div class="usp-header">
      <label :for="id">
        {{ label }}
        <span v-if="info" class="form-group__info" v-tippy="{ content: info }">
          <BaseIcon name="info" size="14" />
        </span>
      </label>
    </div>

    <EmptyState
      v-if="!modelValue.length"
      title="Nog geen USPs toegevoegd"
      description="Voeg USPs toe om je product te onderscheiden"
    >
      <Btn variant="secondary" @click="addUSP" type="button" size="sm" icon="plus">
        USP toevoegen
      </Btn>
    </EmptyState>

    <div v-else class="usp-list">
      <div
        v-for="(usp, index) in modelValue"
        :key="index"
        class="usp-item"
        :class="{ 'usp-item--empty': !usp.trim() }"
      >
        <div class="usp-icon">
          <BaseIcon name="check" size="14" />
        </div>
        <input
          :id="`${id}_${index}`"
          v-model="modelValue[index]"
          @input="handleInput(index, $event)"
          @blur="handleBlur"
          type="text"
          :placeholder="`USP ${index + 1}`"
          :disabled="disabled"
          class="usp-input"
        />
        <Btn
          variant="plain"
          size="sm"
          @click="removeUSP(index)"
          type="button"
          class="remove-usp-btn"
        >
          <BaseIcon name="xmark" size="14" />
        </Btn>
      </div>
      <Btn
        v-if="modelValue.length < max"
        variant="tertiary"
        size="sm"
        @click="addUSP"
        type="button"
        class="add-usp-btn"
      >
        <BaseIcon name="plus" size="14" />
        USP toevoegen
      </Btn>
    </div>

    <div v-if="modelValue.length >= max" class="max-reached">
      <small>Maximum van {{ max }} USPs bereikt</small>
    </div>

    <span v-if="localError" class="form-group__error">{{ localError }}</span>
  </div>
</template>

<script>
  import { ref, watch } from 'vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import Btn from '@/components/partials/Btn.vue';
  import EmptyState from '@/components/partials/EmptyState.vue';

  export default {
    name: 'USPInput',
    components: {
      BaseIcon,
      Btn,
      EmptyState,
    },
    props: {
      id: {
        type: String,
        required: true,
      },
      label: {
        type: String,
        required: true,
      },
      modelValue: {
        type: Array,
        default: () => [],
      },
      error: {
        type: String,
        default: '',
      },
      rules: {
        type: Array,
        default: () => [],
      },
      max: {
        type: Number,
        default: 3,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      info: {
        type: String,
        default: null,
      },
    },
    emits: ['update:modelValue', 'validate'],
    setup(props, { emit }) {
      const localError = ref(props.error);
      const touched = ref(false);

      const handleInput = (index, event) => {
        const newValue = [...props.modelValue];
        newValue[index] = event.target.value;
        emit('update:modelValue', newValue);

        // Emit validation event
        emit('validate', {
          isValid: validateUSPs(newValue),
          error: localError.value,
        });
      };

      const handleBlur = () => {
        touched.value = true;
      };

      const addUSP = () => {
        if (props.modelValue.length < props.max) {
          const newValue = [...props.modelValue, ''];
          emit('update:modelValue', newValue);
        }
      };

      const removeUSP = (index) => {
        const newValue = [...props.modelValue];
        newValue.splice(index, 1);
        emit('update:modelValue', newValue);

        // Emit validation event
        emit('validate', {
          isValid: validateUSPs(newValue),
          error: localError.value,
        });
      };

      const validateUSPs = (usps) => {
        // Basic validation - check if any required rules are violated
        if (props.rules.includes('required') && usps.length === 0) {
          localError.value = 'Minimaal één USP is verplicht';
          return false;
        }

        // Check for empty USPs
        const hasEmptyUSPs = usps.some((usp) => !usp.trim());
        if (hasEmptyUSPs) {
          localError.value = 'Alle USPs moeten ingevuld zijn';
          return false;
        }

        // Check for duplicates
        const uniqueUSPs = new Set(usps.map((usp) => usp.trim().toLowerCase()));
        if (uniqueUSPs.size !== usps.length) {
          localError.value = 'USPs moeten uniek zijn';
          return false;
        }

        localError.value = '';
        return true;
      };

      watch(
        () => props.error,
        (newError) => {
          localError.value = newError;
        },
        { immediate: true }
      );

      return {
        localError,
        touched,
        handleInput,
        handleBlur,
        addUSP,
        removeUSP,
      };
    },
  };
</script>

<style scoped lang="scss">
  .form-group {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .usp-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;

    label {
      font-weight: 500;
      color: var(--slate-700);
      font-size: 0.875rem;
      display: flex;
      align-items: center;
      gap: 0.25rem;
    }
  }

  .add-usp-btn {
    flex-shrink: 0;
  }

  .usp-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .usp-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.125rem;
    background: white;
    transition: all 0.2s ease;

    &:hover {
      border-color: var(--slate-300);
      // box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }
  }

  .usp-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: var(--green-100);
    color: var(--green-600);
    flex-shrink: 0;

    .usp-item--empty & {
      background: var(--red-100);
      color: var(--red-600);
    }
  }

  .usp-input {
    flex: 1;
    border: none;
    outline: none;
    background: transparent;
    font-size: 0.875rem;
    color: var(--slate-700);
    padding: 0.25rem 0;

    &::placeholder {
      color: var(--slate-400);
    }

    &:focus {
      color: var(--slate-900);
    }

    &:disabled {
      color: var(--slate-500);
      cursor: not-allowed;
    }
  }

  .remove-usp-btn {
    flex-shrink: 0;
    opacity: 0.7;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 1;
    }
  }

  .max-reached {
    text-align: center;
    padding: 0.5rem;
    background: var(--amber-50);
    border: 1px solid var(--amber-200);
    border-radius: 6px;
    color: var(--amber-700);

    small {
      font-size: 0.75rem;
      display: block;
    }
  }

  .form-group__info {
    color: var(--slate-400);
    cursor: help;
  }

  .form-group__error {
    color: var(--red-600);
    font-size: 0.75rem;
    font-weight: 500;
  }

  .form-group--has-error {
    .usp-item {
      border-color: var(--red-300);
    }
  }

  .form-group--has-success {
    .usp-item {
      border-color: var(--green-300);
    }
  }
</style>
